import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    useMediaQuery,
    Skeleton,
    InputAdornment,
    IconButton,
} from '@mui/material';
import Sidebar from '../../layouts/Sidebar/Sidebar';
import getTheme from '../../theme/theme';
import { PinnacleThemes } from '../../theme/types';
import { getReferenceById, getReferences, searchReferences } from '../../api/services/references';
import { Reference } from '../../api/interfaces/Reference';
import { handleApiError } from '../../utils/errorHandler';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import StarIcon from '@mui/icons-material/Star';
import SearchIcon from '@mui/icons-material/Search';
import { ClickableCard, ClickableCardBase, ImageContainer } from '../HomePage/partials/VideoListItem/VideoListItem.styled';
import PinnacleSearchField from '../../components/PinnacleSearchField/PinnacleSearchField';
import { useDebounce } from 'use-debounce';
import { profile } from '../../api/services/user';
import { isVideoPlayable } from '../../utils/videoPlayable';
import ReadReferenceDialog from '../../layouts/ReadReferenceDialog/ReadReferenceDialog';
import { PinnacleChip } from '../../components/PinnacleChip';
import { useNavigate } from 'react-router-dom';
import { isReferenceReadable } from '../../utils/referenceReadable';

const placeholderCount = 8;
const placeholders = Array.from({ length: placeholderCount });

const ReferencesPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const isMobile = useMediaQuery('(max-width:600px)');
    const [references, setReferences] = useState<Reference[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
    const [isAccountTrial, setIsAccountTrial] = useState<boolean>(false);
    const [unreadNotification, setUnreadNotification] = useState<number>(0);
    const [selectedReference, setSelectedReference] = useState<Reference | null>(null);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [dialogLoading, setDialogLoading] = useState(true);

    const fetchProfile = useCallback(async () => {
        try {
            const response = await profile();
            const user = response.data.user;
            setIsAccountTrial(user.isTrial ?? false);
            setUnreadNotification(response.data.notifications);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const fetchReferences = useCallback(
        async (pageNo = 1, reset = false) => {
            setLoading(true);
            try {
                const referencesResponse = await getReferences();
                if (reset) {
                    setReferences(referencesResponse.data || []);
                } else {
                    setReferences((prev) => [...prev, ...referencesResponse.data]);
                }
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({ details: msg, type: NotificationTypes.Error });
                    },
                });
            } finally {
                setLoading(false);
            }
        },
        []
    );

    const fetchSearchResults = useCallback(async () => {
        try {
            setLoading(true);
            const referencesResponse = await searchReferences({ keyword: debouncedSearchTerm, isFavorite: false });
            setReferences(referencesResponse.data || []);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
        } finally {
            setLoading(false);
        }
    }, [debouncedSearchTerm]);

    useEffect(() => {
        fetchProfile();
        if (debouncedSearchTerm.trim() !== '') {
            setReferences([]);
            fetchSearchResults();
        } else {
            setReferences([]);
            fetchReferences(1, true);
        }
    }, [debouncedSearchTerm, fetchReferences, fetchSearchResults, fetchProfile]);

    const handleOpenReference = async (selectedReference: Reference, readable: boolean) => {
        if (readable) {
            try {
                // Set initial state using selectedReference to avoid flicker
                setSelectedReference(selectedReference);

                setDialogLoading(true);
                setDialogOpen(true);
                const referenceResponse = await getReferenceById(selectedReference._id);
                setSelectedReference(referenceResponse.data);
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({ details: msg, type: NotificationTypes.Error });
                    },
                });
                setDialogOpen(false);
            } finally {
                setDialogLoading(false);
            }
        }
    };

    return (
        <>
            <main style={{ display: 'flex', width: '100%', minHeight: '100vh', backgroundColor: 'rgba(0, 8, 15, 1)' }}>
                <Sidebar unreadNotification={!!unreadNotification && unreadNotification > 0} />

                <Box
                    component="section"
                    sx={{
                        flexGrow: 1,
                        width: '100%',
                        ml: !isMobile ? '72px' : 0,
                        overflowX: 'hidden',
                        mt: !isMobile ? 0 : 7,
                    }}
                >
                    <Box
                        sx={{
                            mt: { xs: 4, sm: 6 },
                            mb: 4,
                            px: { xs: 2, sm: 3 },
                            width: '100%',
                        }}
                    >
                        {/* Header and Search row */}
                        <Box sx={{ mb: 4 }}>
                            {isMobile ? (
                                <>
                                    {/* Row 1: References and My Favorites */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        <Typography variant="h4" fontWeight={600}>
                                            References
                                        </Typography>

                                        <IconButton>
                                            <PinnacleChip
                                                label="My Favorites"
                                                size="medium"
                                                sx={{
                                                    pointerEvents: 'auto',
                                                    '& .MuiChip-deleteIcon': {
                                                        color: theme.palette.secondary.main,
                                                    },
                                                }}
                                                deleteIcon={<StarIcon sx={{ fontSize: 20 }} />}
                                                onDelete={() => { }}
                                                variant="filled"
                                                onClick={() => navigate('/my-favorite-references')}
                                            />
                                        </IconButton>
                                    </Box>

                                    {/* Row 2: Search Field centered */}
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <PinnacleSearchField
                                            variant="outlined"
                                            label="Search"
                                            placeholder="enter title of references here..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            sx={{ width: '100%' }}
                                            slotProps={{
                                                input: {
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <SearchIcon sx={{ color: 'var(--color-gray-500)' }} />
                                                        </InputAdornment>
                                                    ),
                                                },
                                            }}
                                        />
                                    </Box>
                                </>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'nowrap',
                                    }}
                                >
                                    <Typography variant="h4" fontWeight={600}>
                                        References
                                    </Typography>

                                    <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                                        <PinnacleSearchField
                                            variant="outlined"
                                            label="Search"
                                            placeholder="enter title of references here..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            sx={{ width: '55%' }}
                                            slotProps={{
                                                input: {
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <SearchIcon sx={{ color: 'var(--color-gray-500)' }} />
                                                        </InputAdornment>
                                                    ),
                                                },
                                            }}
                                        />
                                    </Box>

                                    <IconButton>
                                        <PinnacleChip
                                            label="My Favorites"
                                            size="medium"
                                            sx={{
                                                pointerEvents: 'auto',
                                                '& .MuiChip-deleteIcon': {
                                                    color: theme.palette.secondary.main,
                                                },
                                            }}
                                            deleteIcon={<StarIcon sx={{ fontSize: 20 }} />}
                                            onDelete={() => { }}
                                            variant="filled"
                                            onClick={() => navigate('/my-favorite-references')}
                                        />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>


                        {/* REFERENCE ITEMS */}
                        {loading || references.length > 0 ? (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: 'repeat(auto-fill, minmax(160px, 160px))',
                                    },
                                    gap: 2,
                                    width: '100%',
                                    placeContent: 'center'
                                }}
                            >
                                {loading
                                    ? placeholders.map((_, idx) => (
                                        <Box key={`skeleton-${idx}`} sx={{ width: '100%', borderRadius: 2 }}>
                                            <Skeleton
                                                variant="rectangular"
                                                width="100%"
                                                height={isMobile ? 260 : 211}
                                                sx={{ borderRadius: 2 }}
                                            />
                                            <Skeleton width="80%" height={20} sx={{ mt: 1 }} />
                                        </Box>
                                    ))
                                    : references.map((reference) => {
                                        const readable = isReferenceReadable(
                                            isAccountTrial ?? false,
                                            reference.isTrial ?? false
                                        );
                                        return (
                                            <Box key={reference._id} sx={{ width: '100%' }}>
                                                <ClickableCardBase onClick={() => handleOpenReference(reference, readable)}>
                                                    <ClickableCard>
                                                        <ImageContainer
                                                            playable={readable}
                                                            sx={{
                                                                width: '100%',
                                                                height: isMobile ? '260px' : '210px',
                                                                position: 'relative',
                                                                mx: 'auto',
                                                            }}
                                                        >
                                                            <Box
                                                                component="img"
                                                                src={reference.thumbnail}
                                                                alt={reference.title}
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                    borderRadius: '12px',
                                                                    filter: readable ? 'none' : 'brightness(60%)',
                                                                    transition: 'filter 0.2s',
                                                                }}
                                                            />

                                                            {(readable && reference.isFavorite) && (
                                                                <Box
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: 4,
                                                                        left: 4,
                                                                        zIndex: 3,
                                                                        backgroundColor: 'rgba(0,0,0,0.6)',
                                                                        borderRadius: '2.4px',
                                                                        padding: '2.4px',
                                                                    }}
                                                                >
                                                                    <StarIcon sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
                                                                </Box>
                                                            )}

                                                            {!readable && (
                                                                <Box
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                        left: 0,
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        background: 'rgba(0, 0, 0, 0.6)',
                                                                        zIndex: 2,
                                                                        borderRadius: '12px',
                                                                    }}
                                                                >
                                                                    <LockOutlinedIcon
                                                                        sx={{
                                                                            fontSize: 45,
                                                                            color: theme.palette.common.white,
                                                                            borderRadius: '50%',
                                                                            padding: 0.5,
                                                                        }}
                                                                    />
                                                                </Box>
                                                            )}
                                                        </ImageContainer>

                                                        <Box sx={{ textAlign: 'left', mt: 1, width: '100%' }}>
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    display: '-webkit-box',
                                                                    WebkitBoxOrient: 'vertical',
                                                                    WebkitLineClamp: 2,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    maxWidth: '100%',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {reference.title}
                                                            </Typography>
                                                        </Box>
                                                    </ClickableCard>
                                                </ClickableCardBase>
                                            </Box>
                                        );
                                    })}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '300px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography variant="h5" sx={{ textAlign: 'center' }}>
                                    No references found.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </main>
            <ReadReferenceDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                reference={selectedReference}
                isLoading={dialogLoading}
                onFavoriteToggle={(id, isFav) => {
                    setReferences((prev) =>
                        prev.map((ref) =>
                            ref._id === id ? { ...ref, isFavorite: isFav } : ref
                        )
                    );
                    if (selectedReference && selectedReference._id === id) {
                        setSelectedReference({ ...selectedReference, isFavorite: isFav });
                    }
                }}
                referenceThumbnail={selectedReference?.thumbnail}
            />
        </>
    );
};

export default ReferencesPage;
